<script setup lang="ts">
import { computed, ComputedRef, onMounted, ref, watch } from 'vue';
import { useRouter } from 'vue-router';
import useHasLibrary from '@/composables/use-has-library';
import { useLocale } from '@/plugins/translation';
import { Filter, SearchFilters, SearchPanel, SortKey, useSearch, useFilters } from '@ilteducation/content-search';
import useLMGFilterFields from '@/composables/use-lmg-filter-fields';
import { useDialogFocus } from '@ilteducation/components';
import { useDisplay } from 'vuetify';
import { AvailabilityRealmsFilter, CategoryFilter } from '@ilteducation/content-search/src/content-filters';
import useSubscriptions from '@/foundations/helpers/use-subscriptions';
import type { BookV1 } from '@ilteducation/content-registry-types';
import { groupContentByOriginalTitle } from '@ilteducation/content';
import { storeToRefs } from 'pinia';
import { useUserContext } from '@ilteducation/ilt-auth';
import ContentWithFiltersLayout from '@/layouts/ContentWithFiltersLayout.vue';
import BookSearchResults from '@/components/bookshelf/BookSearchResults.vue';

const router = useRouter();
const hasLibrary = useHasLibrary();
const { smAndDown } = useDisplay();
const subscriptions = useSubscriptions();
const userContext = useUserContext();
const { realm } = storeToRefs(userContext);

const searchStore = useSearch<BookV1>('Book');
const { next } = searchStore;
const { result, isLoading, hasMore } = storeToRefs(searchStore);

const limit = 100;
const NATIVE_LANGUAGE_GUIDE_CATEGORY_ID = '7F4J48ZJtrftw967TwLRv';
const sortFields = ['lastAdded', 'titleDesc', 'titleAsc'] as SortKey[];
const isFiltersVisible = ref<boolean>(!smAndDown.value);
const { rememberFocus } = useDialogFocus([isFiltersVisible]);

const { removeAllFilters } = useFilters();
const LMGfilterFields = useLMGFilterFields();
const filterFields = computed(() => [...LMGfilterFields.value]);

const filters: ComputedRef<Filter> = computed(() => ({
  ...CategoryFilter(NATIVE_LANGUAGE_GUIDE_CATEGORY_ID),
  ...AvailabilityRealmsFilter(realm.value),
}));

onMounted(async () => {
  if (!hasLibrary) {
    await router.push('/');
  }
});
const { t, l } = useLocale();

const deduplicatedBooks = ref<BookV1[]>([]);
watch(
  () => result.value,
  () => {
    deduplicatedBooks.value = groupContentByOriginalTitle(result.value) as BookV1[];
  },
  { immediate: true },
);

const toggleFilter = () => {
  if (!isFiltersVisible.value) {
    rememberFocus();
  }
  isFiltersVisible.value = !isFiltersVisible.value;
};
</script>

<template>
  <ContentWithFiltersLayout
    :is-filters-visible="isFiltersVisible"
    @toggle-filter="toggleFilter"
    @remove-all-filters="removeAllFilters"
  >
    <template #title>
      <h1 class="text-secondary">{{ t(l.guides.title) }}</h1>
      <span class="show-more-selection text-secondary">{{ t(l.guides.introduction_paragraph) }}</span>
    </template>
    <template #top-panel>
      <SearchPanel
        :sort-fields="sortFields"
        selected-sort-option="lastAdded"
        :filters="filters as Filter"
        :filter-fields="filterFields"
        :limit="limit"
        content-type="Book"
        :focused="false"
        :estimated-nr-grid-cells="100"
        @toggleFilter="toggleFilter"
      />
    </template>
    <template #filters="{ filterTop }">
      <SearchFilters
        content-type="Book"
        :filter-fields="filterFields"
        :current-subscription="subscriptions[0].type"
        :filter-top="filterTop"
      />
    </template>
    <template #content>
      <BookSearchResults :is-loading="isLoading" :books="deduplicatedBooks" :has-more="hasMore" @load-more="next" />
    </template>
  </ContentWithFiltersLayout>
</template>
